import React from 'react';
import { Button } from '../../../components/ui/button';
import { Check, AlertCircle } from 'lucide-react';

/** Mirror of ClientForm.evaluateStep — kept here so the review can highlight
 * exactly what still blocks submission. */
function requiredChecks(formData) {
  const c = formData.contact || {};
  const employers = (formData.employment && formData.employment.employers) || [];
  return [
    {
      label: 'Legal First Name (Contact Details)',
      ok: !!(c.firstName || '').trim(),
    },
    {
      label: 'Legal Last Name (Contact Details)',
      ok: !!(c.lastName || '').trim(),
    },
    {
      label: 'At least one Employment entry',
      ok: employers.some((e) => e && (
        (e.employer || '').toString().trim() ||
        (e.employmentIncome || '').toString().trim() ||
        (e.totalIncome || '').toString().trim() ||
        (e.liquidNetWorth || '').toString().trim()
      )),
    },
  ];
}

export default function ReviewStep({ formData, files, onSubmit, submitting }) {
  const checks = requiredChecks(formData);
  const allOk = checks.every((c) => c.ok);

  return (
    <div data-testid="review-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Review & Submit
      </h2>
      <p className="text-[#475569] mb-6">
        Please review your information before submitting. You can go back to any step to make changes.
      </p>

      {/* Required fields checklist */}
      <div className={`mb-6 rounded-md border p-4 ${allOk ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`} data-testid="review-required-checklist">
        <h3 className="font-medium text-[#0f172a] mb-3 flex items-center gap-2">
          {allOk ? <Check className="w-4 h-4 text-green-700" /> : <AlertCircle className="w-4 h-4 text-yellow-700" />}
          {allOk ? 'All required fields complete' : 'A few things still needed to submit'}
        </h3>
        <ul className="space-y-1.5 text-sm">
          {checks.map((c) => (
            <li key={c.label} className="flex items-center gap-2">
              {c.ok
                ? <Check className="w-3.5 h-3.5 text-green-700 flex-shrink-0" />
                : <span className="w-3.5 h-3.5 rounded-full border border-yellow-600 flex-shrink-0" />}
              <span className={c.ok ? 'text-[#475569]' : 'text-yellow-800 font-medium'}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-6 mb-8">
        <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
          <h3 className="font-medium text-[#0f172a] mb-2">Contact Details</h3>
          <p className="text-sm text-[#475569]">
            {formData.contact?.firstName} {formData.contact?.lastName}
          </p>
          <p className="text-sm text-[#475569]">{formData.contact?.email}</p>
        </div>

        {formData.employment?.employers?.length > 0 && (
          <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
            <h3 className="font-medium text-[#0f172a] mb-2">Employment</h3>
            <p className="text-sm text-[#475569]">
              {formData.employment.employers.length} employer(s) listed
            </p>
          </div>
        )}

        {files.length > 0 && (
          <div className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0]">
            <h3 className="font-medium text-[#0f172a] mb-2">Attachments</h3>
            <p className="text-sm text-[#475569]">{files.length} file(s) uploaded</p>
          </div>
        )}
      </div>

      <Button
        onClick={onSubmit}
        disabled={submitting || !allOk}
        className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white font-medium py-3 disabled:opacity-50"
        data-testid="submit-form-button"
        title={!allOk ? 'Complete the required fields above to enable submit' : ''}
      >
        {submitting ? 'Submitting...' : allOk ? 'Submit Client Profile' : 'Complete required fields to submit'}
      </Button>
    </div>
  );
}

